import React, { useCallback } from "react";
import "../../styles/ExpressionBuilder.css";
import RuleComponent from "../RuleComponent/RuleComponent";
import GroupActions from "./GroupActions/GroupActions";
import GroupHeader from "./GroupHeader/GroupHeader";
import OperatorButtons from "./OperatorButtons/OperatorButtons";

/**
 * Component for rendering a group of rules/groups with logical operators
 * @param {Object} group - Group configuration
 * @param {Function} onUpdate - Update handler
 * @param {Function} onRemove - Remove handler
 * @param {Array} availableFields - Available workflow variables
 * @param {boolean} isRoot - Whether this is the root group
 * @param {Object} fieldValidators - Validation functions for fields
 * @returns {JSX.Element} Group component
 * @author Abhishek Dhillon
 * @parent SwitchNodePopup
 */
const GroupComponent = ({ group, onUpdate, onRemove, availableFields = [], isRoot = false, fieldValidators = {} }) => {
  /**
   * Updates group properties
   * @param {Object} newProps - New properties
   * @author Abhishek Dhillon
   */
  const updateGroup = (newProps) => {
    onUpdate({ ...group, ...newProps });
  };

  /**
   * Updates a child element
   * @param {string} childId - Child ID
   * @param {Function} updateFn - Update function
   * @author Abhishek Dhillon
   */
  const updateChild = (childId, updateFn) => {
    const newChildren = group?.children?.map((child) => (child?.id === childId ? updateFn(child) : child)) || [];
    updateGroup({ children: newChildren });
  };

  /**
   * Removes a child element
   * @param {string} childId - Child ID to remove
   * @author Abhishek Dhillon
   */
  const removeChild = (childId) => {
    const newChildren = group?.children?.filter((child) => child?.id !== childId) || [];
    updateGroup({ children: newChildren });
  };

  return (
    <div className={`expression-builder-group ${isRoot ? "expression-builder-group-root border" : "expression-builder-group-nested border"}`}>
      {/* Group Header Component */}
      <GroupHeader group={group} onRemove={onRemove} isRoot={isRoot} />

      <div className='expression-builder-group-children'>
        {group?.children?.map((child, index) => (
          <React.Fragment key={child?.id}>
            {/* Logical Operator between children */}
            {index > 0 && <OperatorButtons group={group} onUpdate={updateGroup} isRoot={isRoot} />}

            {/* Render Rule or Nested Group */}
            {child?.type === "RULE" && (
              <RuleComponent
                rule={child}
                onUpdate={(newRule) => updateChild(child.id, () => newRule)}
                onRemove={() => removeChild(child.id)}
                availableFields={availableFields}
                fieldValidators={fieldValidators}
              />
            )}
            {child?.type === "GROUP" && (
              <GroupComponent
                group={child}
                onUpdate={(newGroup) => updateChild(child.id, () => newGroup)}
                onRemove={() => removeChild(child.id)}
                availableFields={availableFields}
                fieldValidators={fieldValidators}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Group Actions Component */}
      <GroupActions group={group} onUpdate={updateGroup} availableFields={availableFields} isRoot={isRoot} />
    </div>
  );
};

export default GroupComponent;
